import React, { useState, useEffect, useRef } from 'react';
import { useApp } from '../context/AppContext';
import { triggerHaptic } from '../services/purrSynthesizer';
import { soundcheckSynth,
  calculateStagePower,
  applyTungstenSoftRamp,
  X32,
  buildEnttecProDmxPacket,
  buildArtNetDmxPacket,
  StageFixtureInfo
} from '../services/stageProtocol';
import { sendUsbCommand, sendUsbDmx } from '../services/usbSerial';
import { fetchFrigateCameras, getFrigateSnapshotUrl, CameraInfo, controlCameraPtz, fetchDehumidifierStatus, controlDehumidifier, fetchHardwareAutodetect, DehumidifierState, pollNfcTag } from '../services/api';
import { Sliders,
  Wind,
  Flame,
  Video,
  Usb,
  Volume2,
  VolumeX,
  Play,
  Square,
  RefreshCw,
  Power,
  ShieldAlert,
  Zap,
  Radio,
  Sparkles,
  Maximize2
} from 'lucide-react';

export const DeviceDecksView: React.FC = () => {
  const [activeDeck, setActiveDeck] = useState<'sound' | 'lighting' | 'camera' | 'serial' | 'appliance'>('sound');
  const [autodetectRig, setAutodetectRig] = useState<{ name: string; deck: 'sound' | 'lighting' | 'camera' | 'serial' | 'appliance'; detail?: string } | null>(null);
  const [dehumidifier, setDehumidifier] = useState<DehumidifierState | null>(null);
  const [targetHum, setTargetHum] = useState<number>(50);
  const [fanSpeed, setFanSpeed] = useState<number>(40);
  const [isUpdatingDehum, setIsUpdatingDehum] = useState<boolean>(false);
  const [nfcTag, setNfcTag] = useState<any>(null);
  const [isScanningNfc, setIsScanningNfc] = useState(false);

  // === 1. SOUND RIG / X32 CONSOLE STATE ===
  const [mainFader, setMainFader] = useState(0.75); // 0 - 1.0
  const [mainMuted, setMainMuted] = useState(false);
  const [dcaFaders, setDcaFaders] = useState([
    { id: 1, name: 'DCA 1: Drums', val: 0.70, muted: false },
    { id: 2, name: 'DCA 2: Bass/Gtr', val: 0.75, muted: false },
    { id: 3, name: 'DCA 3: Vocals', val: 0.80, muted: false },
    { id: 4, name: 'DCA 4: Horns/FX', val: 0.65, muted: false },
  ]);
  const [synthPlaying, setSynthPlaying] = useState<string | null>(null);

  // === 2. LIGHTING RIG / DMX STATE ===
  const [grandMaster, setGrandMaster] = useState(100);
  const [isDeadBlackout, setIsDeadBlackout] = useState(false);
  const [fixtures, setFixtures] = useState<StageFixtureInfo[]>([
    { name: 'Altman Leko #1', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 80, channel: 1 },
    { name: 'Altman Leko #2', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 80, channel: 2 },
    { name: 'Altman Leko #3', type: 'altman_360q_500w', wattsPerUnit: 500, count: 1, currentLevel: 60, channel: 3 },
    { name: 'Par 64 Narrow', type: 'par64_500w', wattsPerUnit: 500, count: 2, currentLevel: 40, channel: 5 },
    { name: 'RGB Wash Bank', type: 'led_rgb_par', wattsPerUnit: 120, count: 4, currentLevel: 70, channel: 7 },
  ]);
  const [dboHoldProgress, setDboHoldProgress] = useState(0);
  const dboTimerRef = useRef<NodeJS.Timeout | null>(null);

  // === 3. CAMERA FLEET STATE ===
  const [cameras, setCameras] = useState<CameraInfo[]>([]);
  const [selectedCamId, setSelectedCamId] = useState<string>('e-roto_1');
  const [camCacheBuster, setCamCacheBuster] = useState(Date.now());

  // === 4. SERIAL BENCH STATE ===
  const [baud, setBaud] = useState(115200);
  const [dtrState, setDtrState] = useState(false);
  const [rtsState, setRtsState] = useState(false);
  const [serialOutput, setSerialOutput] = useState<string[]>([
    '[Serial Logic] CP2102 UART bridge ready @ 115200 8N1.',
    '[Serial Logic] Pin states: DTR=0, RTS=0, CTS=1, DSR=1.'
  ]);

  useEffect(() => {
    fetchFrigateCameras().then((cams) => {
      setCameras(cams);
      if (cams.length > 0 && !cams.find(c => c.id === selectedCamId)) {
        setSelectedCamId(cams[0].id);
      }
    });
  }, []);

  useEffect(() => {
    fetchHardwareAutodetect().then((rep) => {
      if (rep?.phone_hardware?.usb_devices?.length > 0) {
        const d = rep.phone_hardware.usb_devices[0];
        setAutodetectRig({ name: d.productName || 'USB Serial Adapter', deck: 'serial', detail: `${d.vendorId || ''}:${d.productId || ''}` });
      } else if (rep?.homelab_iot?.dehumidifier?.online) {
        setAutodetectRig({ name: 'Midea Dehumidifier', deck: 'appliance', detail: `${rep.homelab_iot.dehumidifier.current_humidity || 48}% RH • 192.168.68.65` });
      }
    });

    fetchDehumidifierStatus().then((d) => {
      setDehumidifier(d);
      if (d.target_humidity) setTargetHum(d.target_humidity);
      if (d.fan_speed) setFanSpeed(d.fan_speed);
    });

    const handleTag = (e: any) => {
      if (e.detail) {
        triggerHaptic([30, 50, 30]);
        setNfcTag(e.detail);
      }
    };
    window.addEventListener('fissal:nfc_tag_detected', handleTag);
    return () => window.removeEventListener('fissal:nfc_tag_detected', handleTag);
  }, []);

  const handleScanNfc = async () => {
    triggerHaptic(20);
    setIsScanningNfc(true);
    const res = await pollNfcTag(5000);
    setIsScanningNfc(false);
    if (res && res.tag_found) {
      triggerHaptic([30, 50, 30]);
      setNfcTag(res);
    }
  };

  const handleToggleDehumPower = async () => {
    if (!dehumidifier) return;
    triggerHaptic(20);
    setIsUpdatingDehum(true);
    const nextPower = dehumidifier.running ? 'off' : 'on';
    await controlDehumidifier({ power: nextPower });
    const fresh = await fetchDehumidifierStatus();
    setDehumidifier(fresh);
    setIsUpdatingDehum(false);
  };

  const handleTargetHumChange = async (val: number) => {
    setTargetHum(val);
    triggerHaptic(15);
    setIsUpdatingDehum(true);
    await controlDehumidifier({ humidity: val });
    const fresh = await fetchDehumidifierStatus();
    setDehumidifier(fresh);
    setIsUpdatingDehum(false);
  };

  const handleFanSpeedChange = async (speed: number) => {
    setFanSpeed(speed);
    triggerHaptic(15);
    setIsUpdatingDehum(true);
    await controlDehumidifier({ fan_speed: speed });
    const fresh = await fetchDehumidifierStatus();
    setDehumidifier(fresh);
    setIsUpdatingDehum(false);
  };

  // --- Sound Rig Handlers ---
  const handleFaderChange = (id: number, val: number) => {
    setDcaFaders((prev) => prev.map((f) => (f.id === id ? { ...f, val } : f)));
    X32.setDcaFader(id, val);
  };

  const handleMuteToggle = (id: number) => {
    triggerHaptic(20);
    setDcaFaders((prev) =>
      prev.map((f) => {
        if (f.id === id) {
          const next = !f.muted;
          X32.setDcaMute(id, next);
          return { ...f, muted: next };
        }
        return f;
      })
    );
  };

  const handleSoundcheckTone = (type: '1khz' | 'pink') => {
    if (synthPlaying === type) {
      soundcheckSynth.stop();
      setSynthPlaying(null);
    } else {
      triggerHaptic(25);
      if (type === '1khz') soundcheckSynth.play1kHzTone(0.3);
      else soundcheckSynth.playPinkNoise(0.3);
      setSynthPlaying(type);
    }
  };

  // --- Lighting Rig Handlers ---
  const handleFixtureChange = (channel: number, val: number) => {
    setFixtures((prev) =>
      prev.map((f) => (f.channel === channel ? { ...f, currentLevel: val } : f))
    );
  };

  // Guarded Dead Blackout (DBO) - requires >=600ms hold
  const startDboHold = () => {
    let progress = 0;
    dboTimerRef.current = setInterval(() => {
      progress += 10;
      setDboHoldProgress(progress);
      if (progress >= 100) {
        clearInterval(dboTimerRef.current!);
        setIsDeadBlackout((prev) => !prev);
        triggerHaptic([40, 60, 40]);
        setDboHoldProgress(0);
      }
    }, 60);
  };

  const cancelDboHold = () => {
    if (dboTimerRef.current) clearInterval(dboTimerRef.current);
    setDboHoldProgress(0);
  };

  // --- PTZ Camera Handlers ---
  const handleCameraPtz = async (dir: 'up' | 'down' | 'left' | 'right') => {
    triggerHaptic(20);
    await controlCameraPtz(selectedCamId, { action: 'step', direction: dir });
  };

  return (
    <div className="flex-1 overflow-y-auto px-3.5 py-3 space-y-4 pb-20">
      {/* Header */}
      <div className="rounded-2xl p-3.5 glass-panel-gold border border-fissal-gold/40 shadow-quantum-glow flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-amber-500/20 border border-fissal-gold/50 flex items-center justify-center text-fissal-gold shadow-quantum-glow">
            <Sliders className="w-6 h-6 animate-pulse" />
          </div>
          <div>
            <h2 className="font-cinzel text-xs font-bold text-white tracking-wider">
              SPECIALIZED CONTROLLER DECKS
            </h2>
            <p className="text-[10px] text-slate-400 font-mono">
              Tactile Guardian Mixer DNA • X32, DMX, Cameras & Serial
            </p>
          </div>
        </div>
      </div>

      {/* Dynamic Contextual Hardware Detection Pill */}
      {autodetectRig && (
        <div className="bg-fissal-gold/15 border border-fissal-gold/40 rounded-2xl p-2.5 flex items-center justify-between animate-in fade-in slide-in-from-top-1 duration-300">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-fissal-gold animate-spin" style={{ animationDuration: '4s' }} />
            <div>
              <span className="text-[10px] font-cinzel font-bold text-fissal-gold tracking-wide">
                CONTEXTUAL RIG DETECTED
              </span>
              <p className="text-[10px] font-mono text-slate-300">
                {autodetectRig.name} {autodetectRig.detail ? `(${autodetectRig.detail})` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={() => {
              setActiveDeck(autodetectRig.deck);
              triggerHaptic(20);
            }}
            className="px-2.5 py-1 rounded-xl bg-fissal-gold text-slate-950 font-bold text-[10px] font-mono shadow-quantum-glow hover:brightness-110 active:scale-95 transition-all"
          >
            SWITCH DECK ➔
          </button>
        </div>
      )}

      {/* Controller Deck Switcher Tabs */}
      <div className="grid grid-cols-5 gap-1.5 bg-black/50 p-1.5 rounded-2xl border border-white/10">
        {[
          { id: 'sound', label: 'Sound', icon: <Volume2 className="w-3.5 h-3.5" /> },
          { id: 'lighting', label: 'Lighting', icon: <Flame className="w-3.5 h-3.5" /> },
          { id: 'camera', label: 'Cameras', icon: <Video className="w-3.5 h-3.5" /> },
          { id: 'serial', label: 'Serial', icon: <Usb className="w-3.5 h-3.5" /> },
          { id: 'appliance', label: 'Appliance', icon: <Wind className="w-3.5 h-3.5" /> },
        ].map((tab) => {
          const isActive = activeDeck === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setActiveDeck(tab.id as any);
                triggerHaptic(15);
              }}
              className={`py-2 px-1 rounded-xl text-xs font-bold flex flex-col items-center justify-center gap-1 transition-all ${
                isActive
                  ? 'bg-fissal-gold text-slate-950 shadow-quantum-glow'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              {tab.icon}
              <span className="text-[10px] tracking-tight">{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* DECK 1: SOUND RIG / BEHRINGER X32 OSC */}
      {activeDeck === 'sound' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
                <Volume2 className="w-4 h-4 text-red-400" />
                BEHRINGER X32 MOTORIZED DCA GROUPS
              </span>
              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                UDP 10023 OSC
              </span>
            </div>

            {/* Motorized Vertical DCA Fader Bank */}
            <div className="grid grid-cols-4 gap-2 pt-2">
              {dcaFaders.map((fader) => (
                <div
                  key={fader.id}
                  className="bg-black/50 border border-white/5 rounded-2xl p-2 flex flex-col items-center gap-2"
                >
                  <span className="text-[10px] font-mono font-bold text-slate-300 text-center truncate w-full">
                    {fader.name.split(':')[1] || fader.name}
                  </span>

                  {/* Vertical Fader Slider */}
                  <div className="h-36 flex items-center justify-center py-2">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={fader.val}
                      onChange={(e) => handleFaderChange(fader.id, parseFloat(e.target.value))}
                      className="h-28 w-2 accent-fissal-gold -rotate-90 cursor-pointer"
                    />
                  </div>

                  <span className="text-[10px] font-mono text-fissal-gold font-bold">
                    {Math.round(fader.val * 100)}%
                  </span>

                  <button
                    onClick={() => handleMuteToggle(fader.id)}
                    className={`w-full py-1.5 rounded-xl font-bold text-[10px] font-mono transition-all ${
                      fader.muted
                        ? 'bg-rose-500 text-white shadow-quantum-glow animate-pulse'
                        : 'bg-white/10 text-slate-400 hover:text-white'
                    }`}
                  >
                    {fader.muted ? 'MUTED' : 'MUTE'}
                  </button>
                </div>
              ))}
            </div>

            {/* Soundcheck Metrology Synth Buttons */}
            <div className="pt-2 border-t border-white/10 flex items-center justify-between">
              <span className="text-[10px] font-mono text-slate-400">Audio Metrology:</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleSoundcheckTone('1khz')}
                  className={`px-2.5 py-1 rounded-xl text-[10px] font-mono border transition-all ${
                    synthPlaying === '1khz'
                      ? 'bg-amber-500 text-slate-950 font-bold border-amber-400'
                      : 'bg-white/5 border-white/10 text-slate-300'
                  }`}
                >
                  {synthPlaying === '1khz' ? 'Stop 1kHz' : '1kHz Tone'}
                </button>
                <button
                  onClick={() => handleSoundcheckTone('pink')}
                  className={`px-2.5 py-1 rounded-xl text-[10px] font-mono border transition-all ${
                    synthPlaying === 'pink'
                      ? 'bg-amber-500 text-slate-950 font-bold border-amber-400'
                      : 'bg-white/5 border-white/10 text-slate-300'
                  }`}
                >
                  {synthPlaying === 'pink' ? 'Stop Pink' : 'Pink Noise'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DECK 2: GUARDIAN STAGE LIGHTING / DMX */}
      {activeDeck === 'lighting' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
                <Flame className="w-4 h-4 text-amber-500" />
                GUARDIAN STAGE LIGHTING RIG
              </span>

              {/* Guarded Dead Blackout (DBO) Button */}
              <button
                onMouseDown={startDboHold}
                onMouseUp={cancelDboHold}
                onTouchStart={startDboHold}
                onTouchEnd={cancelDboHold}
                className={`px-3 py-1.5 rounded-xl font-bold text-xs font-mono relative overflow-hidden transition-all ${
                  isDeadBlackout
                    ? 'bg-rose-600 text-white shadow-quantum-glow animate-pulse'
                    : 'bg-rose-950/80 border border-rose-500/50 text-rose-300 hover:bg-rose-900'
                }`}
              >
                {dboHoldProgress > 0 && (
                  <div
                    className="absolute inset-0 bg-rose-500/40"
                    style={{ width: `${dboHoldProgress}%` }}
                  />
                )}
                <span className="relative z-10">
                  {isDeadBlackout ? 'BLACKOUT ACTIVE' : 'HOLD DBO'}
                </span>
              </button>
            </div>

            {/* Grand Master Slider */}
            <div className="bg-black/40 p-3 rounded-xl border border-white/5 space-y-1.5">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-white font-bold">Grand Master:</span>
                <span className="text-fissal-gold font-bold">{grandMaster}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                value={grandMaster}
                onChange={(e) => setGrandMaster(Number(e.target.value))}
                className="w-full h-2 accent-amber-500 cursor-pointer"
              />
            </div>

            {/* Fixture Bank */}
            <div className="space-y-2 pt-1">
              {fixtures.map((fixture) => (
                <div
                  key={fixture.channel}
                  className="p-2.5 rounded-xl bg-black/40 border border-white/5 space-y-1"
                >
                  <div className="flex items-center justify-between text-xs font-mono">
                    <span className="text-white font-bold">{fixture.name}</span>
                    <span className="text-slate-400">
                      Ch {fixture.channel} • {fixture.currentLevel}%
                    </span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={fixture.currentLevel}
                    onChange={(e) => handleFixtureChange(fixture.channel, Number(e.target.value))}
                    className="w-full h-1.5 accent-amber-400 cursor-pointer"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* DECK 3: PERIMETER SECURITY CAMERAS */}
      {activeDeck === 'camera' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
                <Video className="w-4 h-4 text-sky-400" />
                FRIGATE CAMERA & PTZ CONTROLLER
              </span>
              <button
                onClick={() => setCamCacheBuster(Date.now())}
                className="p-1 rounded-lg text-slate-400 hover:text-white"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Camera Selector Pills */}
            <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-none pb-1">
              {cameras.map((cam) => (
                <button
                  key={cam.id}
                  onClick={() => setSelectedCamId(cam.id)}
                  className={`px-2.5 py-1 rounded-xl text-[10px] font-mono shrink-0 transition-all ${
                    selectedCamId === cam.id
                      ? 'bg-sky-500 text-slate-950 font-bold'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                >
                  {cam.name.split('-')[0] || cam.name}
                </button>
              ))}
            </div>

            {/* Live Camera View */}
            <div className="h-48 bg-black rounded-xl overflow-hidden relative flex items-center justify-center">
              <img
                src={getFrigateSnapshotUrl(selectedCamId, camCacheBuster)}
                alt="Selected Camera"
                className="w-full h-full object-contain"
              />
            </div>

            {/* PTZ Pad */}
            <div className="flex items-center justify-center pt-2">
              <div className="grid grid-cols-3 gap-1.5 w-32">
                <div />
                <button
                  onClick={() => handleCameraPtz('up')}
                  className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                >
                  ▲
                </button>
                <div />
                <button
                  onClick={() => handleCameraPtz('left')}
                  className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                >
                  ◀
                </button>
                <div className="p-2.5 rounded-xl bg-black/40 flex items-center justify-center text-[9px] font-mono text-fissal-gold">
                  PTZ
                </div>
                <button
                  onClick={() => handleCameraPtz('right')}
                  className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                >
                  ▶
                </button>
                <div />
                <button
                  onClick={() => handleCameraPtz('down')}
                  className="p-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-white font-bold text-xs flex items-center justify-center"
                >
                  ▼
                </button>
                <div />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DECK 4: SERIAL LOGIC BENCH */}
      {activeDeck === 'serial' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="glass-panel-gold rounded-2xl p-3.5 border border-fissal-gold/30 shadow-lg space-y-3">
            <div className="flex items-center justify-between">
              <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
                <Usb className="w-4 h-4 text-emerald-400" />
                USB-UART LOGIC & BOOTLOADER LINES
              </span>
              <span className="text-[10px] font-mono text-emerald-400">115200 8N1</span>
            </div>

            {/* DTR / RTS Line Toggles */}
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => {
                  triggerHaptic(20);
                  setDtrState(!dtrState);
                }}
                className={`p-3 rounded-xl border text-xs font-mono font-bold flex items-center justify-between transition-all ${
                  dtrState
                    ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300'
                    : 'bg-black/40 border-white/10 text-slate-400'
                }`}
              >
                <span>DTR Line:</span>
                <span>{dtrState ? 'HIGH (1)' : 'LOW (0)'}</span>
              </button>

              <button
                onClick={() => {
                  triggerHaptic(20);
                  setRtsState(!rtsState);
                }}
                className={`p-3 rounded-xl border text-xs font-mono font-bold flex items-center justify-between transition-all ${
                  rtsState
                    ? 'bg-emerald-500/20 border-emerald-400 text-emerald-300'
                    : 'bg-black/40 border-white/10 text-slate-400'
                }`}
              >
                <span>RTS Line:</span>
                <span>{rtsState ? 'HIGH (1)' : 'LOW (0)'}</span>
              </button>
            </div>

            {/* Output log */}
            <div className="h-44 bg-black/80 rounded-xl border border-white/10 p-2.5 font-mono text-[11px] text-emerald-400 overflow-y-auto space-y-1">
              {serialOutput.map((l, i) => (
                <div key={i}>{l}</div>
              ))}
            </div>

            {/* NFC Hardware Transponder Inspector */}
            <div className="pt-3 border-t border-white/10 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-mono font-bold text-cyan-400 flex items-center gap-1.5">
                  <Radio className="w-3.5 h-3.5 text-cyan-400" />
                  NFC Transponder Interrogator
                </span>
                <button
                  onClick={handleScanNfc}
                  disabled={isScanningNfc}
                  className={`px-3 py-1 rounded-xl text-xs font-mono font-bold border transition-all ${
                    isScanningNfc ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300 animate-pulse' : 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/20'
                  }`}
                >
                  {isScanningNfc ? 'Listening (5s)...' : 'Scan NFC Tag'}
                </button>
              </div>

              {nfcTag && (
                <div className="p-3 bg-black/60 rounded-xl border border-cyan-500/30 font-mono text-xs space-y-1.5">
                  <div className="flex items-center justify-between text-cyan-300 font-bold">
                    <span>UID:</span>
                    <span>{nfcTag.uid || nfcTag.uid_raw || 'Discovered'}</span>
                  </div>
                  {nfcTag.tech_list && (
                    <div className="text-[10px] text-slate-400">
                      Tech: {Array.isArray(nfcTag.tech_list) ? nfcTag.tech_list.map((t: string) => t.split('.').pop()).join(', ') : nfcTag.tech_list}
                    </div>
                  )}
                  {nfcTag.ndef_records && nfcTag.ndef_records.length > 0 && (
                    <div className="pt-1 border-t border-white/10 text-emerald-400 text-[11px] space-y-0.5">
                      {nfcTag.ndef_records.map((r: string, i: number) => (
                        <div key={i}>{r}</div>
                      ))}
                    </div>
                  )}
                  {nfcTag.atqa && (
                    <div className="text-[10px] text-slate-400 flex justify-between">
                      <span>ATQA: {nfcTag.atqa}</span>
                      <span>SAK: {nfcTag.sak}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DECK 5: APPLIANCE DECK (Midea Dehumidifier & Smart IoT) */}
      {activeDeck === 'appliance' && (
        <div className="space-y-4 animate-in fade-in duration-200">
          <div className="glass-panel-gold rounded-2xl p-4 border border-fissal-gold/30 shadow-lg space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-cinzel text-xs font-bold text-white tracking-wider flex items-center gap-2">
                <Wind className="w-4 h-4 text-cyan-400" />
                MIDEA DEHUMIDIFIER (TYPE A1)
              </span>
              <span className="text-[9px] font-mono px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                192.168.68.65 • 100% OFFLINE LAN
              </span>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 gap-2.5">
              <div className="p-3 bg-black/50 rounded-xl border border-white/10 flex flex-col items-center">
                <span className="text-[10px] font-mono text-slate-400">Current Humidity</span>
                <span className="text-2xl font-bold font-mono text-cyan-300 mt-1">
                  {dehumidifier?.current_humidity ? `${dehumidifier.current_humidity}%` : '--'}
                </span>
                <span className="text-[9px] font-mono text-slate-500 mt-0.5">
                  Target: {dehumidifier?.target_humidity || targetHum}%
                </span>
              </div>

              <div className="p-3 bg-black/50 rounded-xl border border-white/10 flex flex-col items-center">
                <span className="text-[10px] font-mono text-slate-400">Ambient Temp</span>
                <span className="text-2xl font-bold font-mono text-amber-300 mt-1">
                  {dehumidifier?.temperature_c ? `${dehumidifier.temperature_c}°C` : '--'}
                </span>
                <span className="text-[9px] font-mono text-slate-500 mt-0.5">
                  {dehumidifier?.temperature_c ? `${Math.round(dehumidifier.temperature_c * 9/5 + 32)}°F` : ''}
                </span>
              </div>
            </div>

            {/* Power Toggle & State */}
            <div className="p-3 bg-black/40 rounded-xl border border-white/10 flex items-center justify-between">
              <div>
                <span className="text-xs font-bold font-mono text-white block">Appliance Power</span>
                <span className="text-[10px] font-mono text-slate-400">
                  Status: {dehumidifier?.running ? 'Humming (Active)' : 'Standby'}
                </span>
              </div>
              <button
                onClick={handleToggleDehumPower}
                disabled={isUpdatingDehum}
                className={`px-4 py-1.5 rounded-xl font-bold font-mono text-xs flex items-center gap-1.5 transition-all ${
                  dehumidifier?.running
                    ? 'bg-cyan-500 text-slate-950 shadow-quantum-glow'
                    : 'bg-white/10 text-slate-300 hover:text-white'
                }`}
              >
                <Power className="w-3.5 h-3.5" />
                {dehumidifier?.running ? 'ON' : 'OFF'}
              </button>
            </div>

            {/* Target Humidity Slider */}
            <div className="space-y-2 p-3 bg-black/40 rounded-xl border border-white/10">
              <div className="flex items-center justify-between text-xs font-mono">
                <span className="text-slate-300">Set Target Humidity:</span>
                <span className="font-bold text-cyan-300">{targetHum}%</span>
              </div>
              <input
                type="range"
                min="35"
                max="85"
                step="5"
                value={targetHum}
                onChange={(e) => handleTargetHumChange(parseInt(e.target.value))}
                className="w-full accent-cyan-400 cursor-pointer"
              />
              <div className="flex justify-between text-[9px] font-mono text-slate-500">
                <span>35% (Dry)</span>
                <span>50% (Comfort)</span>
                <span>85% (Continuous)</span>
              </div>
            </div>

            {/* Fan Speed Multi-Speed Selector */}
            <div className="space-y-1.5 p-3 bg-black/40 rounded-xl border border-white/10">
              <span className="text-[10px] font-mono text-slate-400 uppercase tracking-wider block">
                Fan Velocity
              </span>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { speed: 40, label: 'Low (40%)' },
                  { speed: 60, label: 'Med (60%)' },
                  { speed: 80, label: 'High (80%)' },
                ].map((item) => (
                  <button
                    key={item.speed}
                    onClick={() => handleFanSpeedChange(item.speed)}
                    disabled={isUpdatingDehum}
                    className={`py-1.5 rounded-xl font-mono text-[10px] font-bold border transition-all ${
                      fanSpeed === item.speed
                        ? 'bg-cyan-500/20 border-cyan-400 text-cyan-300'
                        : 'bg-white/5 border-white/10 text-slate-400 hover:text-white'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Reservoir Full Alert */}
            {dehumidifier?.tank_full && (
              <div className="p-2.5 rounded-xl bg-rose-500/20 border border-rose-500/40 flex items-center gap-2 text-rose-300 animate-pulse">
                <ShieldAlert className="w-4 h-4 shrink-0" />
                <span className="text-xs font-mono font-bold">
                  Water Collection Reservoir Full! Empty bucket to resume dehumidification.
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
